<template>
  <GridBlockBase id="conversations-list" :title="t('chat.conversationsList.title')">
    <!-- en mobile, la barre du haut est masquee sur le chat : acces au menu ici -->
    <template v-if="isMobile" #icon>
      <SidebarTrigger class="-ml-2 size-10" data-test-id="chat-sidebar-trigger" />
    </template>
    <template #headerExtra>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        class="ml-auto size-10 md:size-8"
        :aria-label="t('chat.newConversation.open')"
        data-test-id="new-conversation-button"
        @click="isNewConversationOpenRef = true"
      >
        <Plus class="size-4" aria-hidden="true" />
      </Button>
      <NewConversationDialog :open="isNewConversationOpenRef" @update:open="isNewConversationOpenRef = $event" />
    </template>
    <template #body>
      <LoadingContent v-if="isConversationsPendingRef" />
      <EmptyContent
        v-else-if="!isConversationsPendingRef && conversationsRef.length === 0"
        :text="t('chat.conversationsList.empty')"
      >
        <template #icon>
          <ConversationIcon color="black" />
        </template>
      </EmptyContent>
      <div v-else class="conversations-list__content">
        <div class="conversations-list__tools">
          <InputGroup>
            <InputGroupAddon>
              <Search aria-hidden="true" />
            </InputGroupAddon>
            <InputGroupInput
              v-model="searchRef"
              type="search"
              :placeholder="t('chat.conversationsList.searchPlaceholder')"
              :aria-label="t('chat.conversationsList.searchPlaceholder')"
              data-test-id="conversations-search"
            />
          </InputGroup>
          <!-- filtres en onglets (Tabs du design system, comme sur la maquette) -->
          <Tabs :model-value="filterRef" class="self-start" @update:model-value="setFilter">
            <TabsList class="h-11 md:h-9" :aria-label="t('chat.conversationsList.filterLabel')">
              <TabsTrigger
                v-for="filter in FILTERS"
                :key="filter"
                :value="filter"
                :data-test-id="`conversations-filter-${filter}`"
              >
                {{ t(`chat.conversationsList.filter.${filter}`) }}
                <span
                  v-if="filter === 'unread' && unreadConversationsCountRef > 0"
                  class="conversations-list__filter-count"
                >
                  {{ unreadConversationsCountRef }}
                </span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <ConversationItem
          v-for="conversation in visibleConversationsRef"
          :key="conversation.conversationId"
          :conversation="conversation"
        />
        <p v-if="visibleConversationsRef.length === 0" class="conversations-list__no-result" role="status">
          {{ t('chat.conversationsList.noResult') }}
        </p>
      </div>
    </template>
  </GridBlockBase>
</template>

<script setup lang="ts">
import { Plus, Search } from 'lucide-vue-next'
import { computed, ref } from 'vue'

import { Button } from '@/components/ui/button'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group'
import { SidebarTrigger, useSidebar } from '@/components/ui/sidebar'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import useAccount from '@/features/auth/hooks/use-account.hook'
import ConversationItem from '@/features/chat/components/conversations-list/conversation-item/conversation-item.vue'
import NewConversationDialog from '@/features/chat/components/conversations-list/new-conversation-dialog.vue'
import useConversations from '@/features/chat/hooks/use-conversations.hook'
import { normalizeSearchText } from '@/features/chat/utils/chat-search'
import usePrograms from '@/features/programs/hooks/use-programs.hook'
import EmptyContent from '@/features/ui/components/empty-content/empty-content.vue'
import GridBlockBase from '@/features/ui/components/grid-block-base/grid-block-base.vue'
import ConversationIcon from '@/features/ui/components/icons/conversation-icon.vue'
import LoadingContent from '@/features/ui/components/loading-content/loading-content.vue'
import { useI18n } from '@/lib/i18n'

const { t } = useI18n()
const { isMobile } = useSidebar()

const { conversationsRef, isPendingRef: isConversationsPendingRef } = useConversations()
const { accountRef } = useAccount()

// filtres "Tous" / "Non lus" et recherche de conversation (nom, participants, programme)
const FILTERS = ['all', 'unread'] as const
const isNewConversationOpenRef = ref(false)
const filterRef = ref<(typeof FILTERS)[number]>('all')
const setFilter = (value: string | number) => {
  if (FILTERS.includes(value as (typeof FILTERS)[number])) filterRef.value = value as (typeof FILTERS)[number]
}
const searchRef = ref('')
const { programsRef } = usePrograms()
const programNamesRef = computed(
  () => new Map((programsRef.value ?? []).map((program) => [program.id, program.name] as const)),
)

const isUnread = (conversation: (typeof conversationsRef.value)[number]) =>
  conversation.unreadCount > 0 || !conversation.users.find((user) => user.id === accountRef.value?.id)?.hasSeen

const unreadConversationsCountRef = computed(() => conversationsRef.value.filter(isUnread).length)

const visibleConversationsRef = computed(() => {
  const query = normalizeSearchText(searchRef.value.trim())
  return conversationsRef.value.filter((conversation) => {
    if (filterRef.value === 'unread' && !isUnread(conversation)) return false
    if (!query) return true
    // la liste cherche une conversation ; les messages se cherchent dans la conversation ouverte
    const names = [
      conversation.groupName ?? '',
      ...conversation.users.map((user) => `${user.firstName ?? ''} ${user.lastName ?? ''}`),
      conversation.groupId && conversation.programId ? (programNamesRef.value.get(conversation.programId) ?? '') : '',
    ]
    return names.some((text) => normalizeSearchText(text).includes(query))
  })
})
</script>

<style lang="scss">
@import './conversations-list.scss';
</style>
